import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { faBan, faCheck, faPen, faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { forkJoin } from 'rxjs';
import * as _ from 'underscore';
import { ErrorService } from '../../../../@core/utils/error.service';
import { KmlService } from '../../../../@core/utils/kml.service';
import { SessionService } from '../../../../@core/utils/session.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { PoolTypeDTO } from '../../../modules/pick-plan/models/pool-type-dto';
import { PoolService } from '../../services/pool.service';
import { KwFilterDropdownService } from '../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { PoolDetailsComponent } from './pool-details/pool-details.component';
import { PoolListDataSource } from './pool-list-datasource';

@Component({
  selector: 'pool-list',
  templateUrl: './pool-list.component.html',
  styleUrls: ['./pool-list.component.css']
})
export class PoolListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  //#region Properties
  dataSource: PoolListDataSource;
  public editPool: PoolTypeDTO = {};
  public allPooType: PoolTypeDTO[] = [];
  public poolId: number;
  public poolLoading: boolean = false;
  public submitting: any;
  public editActive = false;
  public savingPoolIds: Set<number> = new Set<number>();
  private pendingScrollToPoolId: number = null;
  private highlightedPoolId: number = null;
  public allColumns: ColumnModel[] = [];
  public gridName: string = "PoolConfigGrid";
  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200],
  };
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel",
  };
  public toolbarOptions: string[] = [
    "ExcelExport",
  ];
  public enablePersistence: boolean = true;
  public filterCount: number = 0;

   /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
   displayedColumns = ['name', 'description', 'integrationId', 'isActive', 'edit'];
   //#endregion
  
  //#region Icon Properties
  public faPlus = faPlus;
  public faPen = faPen;
  public faTrash = faTrash;
  public faTimes = faTimes;
  public faCheck = faCheck;
  public faBan = faBan;
  //#endregion

  //#region ViewChild Properties
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  @ViewChild(MatSort, { static: false }) sort: MatSort;

  @ViewChild("grid", { static: true }) 
  grid: GridComponent;

  @ViewChild("actionTemplate", { static: true })
  public actionTemplate: any;

  @ViewChild("isActiveTemplate", { static: true })
  public isActiveTemplate: any;
  //#endregion

  constructor(
    private poolService: PoolService,
    private errorService: ErrorService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private sessionService: SessionService,
    public dialogService: MatDialog,
    private filterService: KwFilterDropdownService
  ){}

  ngOnInit() {
    this.poolLoading = true;
    this.dataSource = new PoolListDataSource(this.paginator, this.sort);

    this.poolService.getPoolTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:data => {
      data = _.sortBy(data, 'name');
      this.dataSource.data = data;
      
      if(this.paginator) this.paginator._changePageSize(this.paginator.pageSize);
      this.poolLoading = false;
    }, error:err => {this.errorService.handleError(err); this.poolLoading = false;}});
    this.loadData();
  }

  loadData() {
    this.poolLoading = true;

    forkJoin([
      this.poolService.getPoolTypes(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (results) => {
        this.allPooType = results[0];

        this.poolLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.poolLoading = false;
      }}
    );
  }

  toolbarClick(args) {
    this.poolLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `PoolExport.xlsx`,
        })
        .then(() => {
          this.poolLoading = false;
        });
    }
  }

  setColumns() {
    this.allColumns = [];

    this.allColumns.push(
      ...[
        {
          field: "id",
          isPrimaryKey: true,
          visible: false,
        },
        {
          field: "name",
          headerText: "Name",
        },
        {
          field: "description",
          headerText: "Description",
        },
        {
          field: "integrationId",
          headerText: "Integration Id",
        },
        {
          headerText: "Action",
          template: this.actionTemplate,
          width: 200,
        },
      ]
    );
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this pool?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.poolLoading = true;
    this.poolService.deletePoolTypeDTO(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        // Keep the filter dropdown cache in sync so other pages drop the deleted pool type immediately
        this.filterService.removeCached('poolType', id);
        this.toastrService.info("Pool type successfully deleted!");
        this.poolLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.poolLoading = false;
      }}
    );
  }

  openDialog(IsEdit: boolean, data?: PoolTypeDTO) {
    const poolTypeDetailsDialog = this.dialogService.open(PoolDetailsComponent, {
      width: "500px",
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      // Clone: never hand the dialog a live reference into allPooType, or its
      // two-way-bound form fields would mutate the grid row before Save.
      this.sessionService.selectedPoolType = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedPoolType = {} as PoolTypeDTO;
    }

    poolTypeDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.pool) {
        if (result.isNew) {
          this.createPoolInBackground(result.pool);
        } else {
          this.savePoolInBackground(result.pool);
        }
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedPoolType = {} as PoolTypeDTO;
    });
  }

  private savePoolInBackground(pool: PoolTypeDTO): void {
    const originalPool = this.allPooType.find((p) => p.id === pool.id);
    const poolToUpdate: PoolTypeDTO = { ...pool, updatedDate: new Date() };

    this.savingPoolIds.add(pool.id);
    this.insertOrReplacePoolRow(poolToUpdate);
    this.refreshGridSoon();

    this.poolService.putPoolTypeDTO(poolToUpdate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingPoolIds.delete(pool.id);
        // 204 No Content leaves `updated` null/undefined - fall back to the pool we sent.
        this.filterService.upsertCached('poolType', updated || poolToUpdate);
        this.insertOrReplacePoolRow(updated || poolToUpdate);
        this.refreshGridSoon();
        this.toastrService.success(
          "Pool details successfully updated!",
          this.poolDisplayName(pool) + " updated!"
        );
      },
      error: (err) => {
        this.savingPoolIds.delete(pool.id);
        if (originalPool) {
          this.insertOrReplacePoolRow(originalPool);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Pool "' + this.poolDisplayName(pool) + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private createPoolInBackground(pool: PoolTypeDTO): void {
    // The pool doesn't exist server-side yet, so show a temporary placeholder
    // row (negative id so it can't collide with a real one) with the saving
    // spinner until the server assigns a real id.
    const tempId = -Date.now();
    const poolToCreate: PoolTypeDTO = { ...pool, createdDate: new Date(), updatedDate: new Date() };

    this.insertOrReplacePoolRow({ ...poolToCreate, id: tempId });
    this.savingPoolIds.add(tempId);

    this.poolService.postPoolTypeDTO(poolToCreate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.removePoolRow(tempId);
        this.savingPoolIds.delete(tempId);
        this.filterService.upsertCached('poolType', created);
        this.insertOrReplacePoolRow(created);
        this.jumpToPoolRow(created.id);
        this.toastrService.success(
          "Pool successfully created!",
          this.poolDisplayName(created) + " created!"
        );
      },
      error: (err) => {
        this.removePoolRow(tempId);
        this.savingPoolIds.delete(tempId);
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Pool "' + this.poolDisplayName(pool) + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private removePoolRow(id: number): void {
    this.allPooType = this.allPooType.filter((p) => p.id !== id);
    this.refreshGridSoon();
  }

  // Pool (unlike Blocks) doesn't maintain its own sorted array - allPooType's
  // index only matches the grid's displayed order when the grid itself has
  // no active sort/filter. Skip the page computation otherwise; the row will
  // still get scrolled to/highlighted whenever it's next rendered on
  // whatever page the user is on.
  private jumpToPoolRow(id: number): void {
    this.highlightedPoolId = id;
    this.pendingScrollToPoolId = id;

    const hasActiveSortOrFilter = (this.grid?.sortSettings?.columns?.length > 0) || this.filterCount > 0;
    if (!this.grid || hasActiveSortOrFilter) {
      return;
    }

    setTimeout(() => {
      const index = this.allPooType.findIndex((p) => p.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allPooType.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedPoolId === id) {
      this.highlightedPoolId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  private poolDisplayName(pool: PoolTypeDTO): string {
    return (pool.name && pool.name.trim().length > 0) ? pool.name.trim() : 'Pool';
  }

  private insertOrReplacePoolRow(pool: PoolTypeDTO): void {
    const index = this.allPooType.findIndex((p) => p.id === pool.id);
    const next = this.allPooType.slice();
    if (index !== -1) {
      next[index] = pool;
    } else {
      next.push(pool);
    }
    this.allPooType = next;
    this.refreshGridSoon();
  }

  // Defers the grid refresh to the next macrotask so Angular has already
  // pushed the updated allPooType/savingPoolIds state into the grid's
  // own [dataSource] binding before we force it to re-render.
  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingPoolIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedPoolId != null && args.data.id === this.highlightedPoolId) {
      args.row.classList.add('row-highlight-new');
    }
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToPoolId != null) {
      const id = this.pendingScrollToPoolId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToPoolId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  clearFilters() {
    window.localStorage.setItem("gridPoolConfigFilters", "");
    this.grid.clearFiltering();
    this.filterCount = 0;
  }
  downloadFiles(args : string){
    this.poolLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `PoolExport.xlsx`,
        })
        .then(() => {
          this.poolLoading = false;
        });
    }
  }
}
